import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { usePoll } from "@/lib/usePoll";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "./_shared";
import { Textarea } from "@/components/ui/textarea";
import {
  MessageSquare, Send, Camera, X, Trash2,
  AtSign, SmilePlus, Smile, ArrowLeft, Phone, Video, MoreHorizontal, Pin, StickyNote, CheckCheck, Plus, Bell,
} from "lucide-react";
import { toast } from "sonner";
import { uploadMany } from "@/lib/imageUpload";
import { UploadingThumb } from "@/components/UploadingThumb";
import { AuthImage } from "@/components/AuthImage";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import {
  MembersStrip, CallSheet, PinnedStrip, AudioBubble, VoiceButton,
  QuickActionsBar, SiteNoteDialog, TaskDialog, FilesTab, TasksTab,
  DetailsTab, LocationPin, SiteNoteChip, usePresence,
} from "@/components/chat/ChatPanels";

const POLL_MS = 5000;
const TYPING_HEARTBEAT_MS = 2500;
const DELETE_WINDOW_MS = 5 * 60 * 1000;
const REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🔥"];
// Curated composer emoji set — keep it short so the picker stays one tap deep.
const COMPOSER_EMOJIS = [
  "😀","😂","🤣","😅","😊","😇","🙂","😍","😘","😜",
  "🤔","😎","😢","😭","😡","🤯","😴","🤤","🤝","🙏",
  "💪","👏","👍","👎","❤️","🔥","💯","✅","❌","⚠️",
  "🚧","🏗️","🛠️","🚛","🪜","⛑️","☀️","☔","🌧️","⛈️",
];

export default function Chat() {
  const { user } = useAuth();
  const isField = user.role === "field";
  const [channels, setChannels] = useState([]);
  const [team, setTeam] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  usePresence();

  const refreshChannels = useCallback(async () => {
    const { data } = await api.get("/chat/channels");
    setChannels(data);
  }, []);

  useEffect(() => { refreshChannels(); }, [refreshChannels]);
  useEffect(() => { api.get("/users").then((r) => setTeam(r.data)).catch(() => {}); }, []);
  // Slow poll for the channel meta (last-message preview, unread badge); paused while the tab is hidden
  usePoll(refreshChannels, POLL_MS * 2, { immediate: false });

  // Org-wide chat only: there's exactly one channel (General).
  const active = channels[0];

  // PWA full-screen mode: field workers always see Messenger-style edge-to-edge.
  const fullScreen = isField && !!active;

  return (
    <div
      data-testid="chat-page"
      className={`flex flex-col ${
        fullScreen
          ? "fixed inset-0 z-[60] h-[100dvh] bg-[#0B1018] lg:static lg:z-auto lg:h-[calc(100dvh-60px)]"
          : isField
            ? "h-[calc(100dvh-160px)] min-h-[440px]"
            : "h-[calc(100dvh-60px)] min-h-[520px]"
      }`}
    >
      {!fullScreen && (
        <div className="px-4 lg:px-10 pt-4 lg:pt-8 pb-2 max-w-5xl w-full mx-auto shrink-0">
          <PageHeader
            tag="/ crew chat"
            title="Team Chat"
            subtitle="One company-wide chat — everyone in the org sees everything. Photos, @mentions, reactions and read receipts."
          />
        </div>
      )}

      <div className={`flex-1 min-h-0 ${fullScreen ? "w-full" : "max-w-5xl w-full mx-auto px-4 lg:px-10 pb-3 lg:pb-6"}`}>
        <div className={`flex flex-col h-full min-h-0 bg-[#0B1018] ${fullScreen ? "" : "border border-zinc-800 rounded-2xl overflow-hidden"}`}>
          {active ? (
            <ChannelView
              key={active.id}
              channel={active}
              user={user}
              team={team}
              onPhotoClick={setLightbox}
              onMessageActivity={refreshChannels}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center p-8">
              <EmptyState title="Loading…" description="Setting up your team chat." />
            </div>
          )}
        </div>
      </div>

      <PhotoLightbox path={lightbox} open={!!lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

/**
 * Avatar — round image (from /files/{path}) with initials fallback. Used in
 * message bubbles and the channel header. Size in pixels.
 */
function Avatar({ name, path, size = 28, className = "" }) {
  const initials = (name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div
      className={`shrink-0 rounded-full bg-[#09090B] text-white flex items-center justify-center overflow-hidden font-display font-black ${className}`}
      style={{ width: size, height: size, fontSize: Math.max(9, Math.floor(size * 0.36)) }}
    >
      {path
        ? <AuthImage path={path} alt={name || ""} className="w-full h-full object-cover" />
        : <span>{initials}</span>}
    </div>
  );
}

function ChannelView({ channel, user, team, onPhotoClick, onMessageActivity }) {
  const isField = user.role === "field";
  const nav = useNavigate();
  const mobileBarClasses = "lg:static";
  const [tab, setTab] = useState("chat"); // chat | files | tasks | details
  const [members, setMembers] = useState(null);
  const [pins, setPins] = useState([]);
  const [callOpen, setCallOpen] = useState(false);
  const [siteNoteFor, setSiteNoteFor] = useState(null); // message obj
  const [taskOpen, setTaskOpen] = useState(false);
  const [tasksRefresh, setTasksRefresh] = useState(0);
  const [openTasks, setOpenTasks] = useState(0);
  const locationRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [reads, setReads] = useState([]); // [{user_id, name, avatar_path, last_read_at}]
  const [typing, setTyping] = useState([]);
  const [draft, setDraft] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]); // in-flight: {key,url,pct,error}
  const [sending, setSending] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [reactPickerFor, setReactPickerFor] = useState(null); // message id
  const [emojiOpen, setEmojiOpen] = useState(false);
  const scrollRef = useRef(null);
  const lastIdRef = useRef(null);
  const textareaRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  // Mark channel read
  const markRead = useCallback(async () => {
    try {
      await api.post(`/chat/channels/${channel.id}/read`, {});
      window.dispatchEvent(new Event("fl:chat-read"));
      onMessageActivity?.();
    } catch { /* ignore */ }
  }, [channel.id, onMessageActivity]);

  // Load + poll messages
  const loadMessages = useCallback(async (silent = false) => {
    try {
      const { data } = await api.get(`/chat/channels/${channel.id}/messages`);
      setMessages(data);
      if (data.length) lastIdRef.current = data[data.length - 1].id;
      if (!silent) {
        setTimeout(() => {
          scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "auto" });
        }, 30);
      } else {
        // Only auto-scroll if user near bottom
        const el = scrollRef.current;
        if (el && (el.scrollHeight - el.scrollTop - el.clientHeight < 100)) {
          setTimeout(() => el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }), 30);
        }
      }
      markRead();
    } catch { /* ignore */ }
  }, [channel.id, markRead]);

  // Load read receipts (who's read up to where)
  const loadReads = useCallback(async () => {
    try {
      const { data } = await api.get(`/chat/channels/${channel.id}/reads`);
      setReads(data);
    } catch { /* ignore */ }
  }, [channel.id]);

  // Poll typing indicator separately (3s cadence — faster than messages
  // so the indicator feels responsive)
  const loadTyping = useCallback(async () => {
    try {
      const { data } = await api.get(`/chat/channels/${channel.id}/typing`);
      setTyping(data);
    } catch { /* ignore */ }
  }, [channel.id]);

  useEffect(() => { loadMessages(false); loadTyping(); loadReads(); }, [loadMessages, loadTyping, loadReads]);
  // New messages keep arriving slowly in a hidden tab; typing and read receipts only matter on screen.
  usePoll(() => loadMessages(true), POLL_MS, { hiddenMs: 30000, immediate: false });
  usePoll(loadTyping, 3000, { immediate: false });
  usePoll(loadReads, POLL_MS, { immediate: false });

  // Compute per-message "seen by" — find the LAST message each reader has
  // seen. We then attach the reader to that message so the seen-by avatars
  // hang under the right bubble (Messenger-style).
  const seenByForMessage = useMemo(() => {
    const out = {};
    if (!messages.length || !reads.length) return out;
    for (const r of reads) {
      // find the latest message they've read (last_read_at >= created_at)
      let lastSeen = null;
      for (const m of messages) {
        if (m.user_id === r.user_id) continue; // don't show self-seen
        if (m.created_at <= r.last_read_at) lastSeen = m.id;
      }
      if (lastSeen) (out[lastSeen] = out[lastSeen] || []).push(r);
    }
    return out;
  }, [messages, reads]);

  // Members / pins / open-task count (slow poll)
  const loadExtras = useCallback(async () => {
    try {
      const [m, p, t] = await Promise.all([
        api.get(`/chat/channels/${channel.id}/members`),
        api.get(`/chat/channels/${channel.id}/pins`),
        api.get("/chat/tasks"),
      ]);
      setMembers(m.data); setPins(p.data); setOpenTasks(t.data.open || 0);
    } catch { /* ignore */ }
  }, [channel.id]);
  usePoll(loadExtras, 30000);

  const togglePin = useCallback(async (msgId) => {
    try {
      await api.post(`/chat/messages/${msgId}/pin`);
      loadExtras();
      setReactPickerFor(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Pin failed"); }
  }, [loadExtras]);

  const sendVoice = useCallback(async (path, secs) => {
    try {
      await api.post(`/chat/channels/${channel.id}/messages`, {
        text: "", photo_paths: [], mentions: [], audio_path: path, audio_seconds: secs,
      });
      await loadMessages(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Voice note failed"); }
  }, [channel.id, loadMessages]);

  const quickPhoto = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { locationRef.current = `${pos.coords.latitude.toFixed(5)},${pos.coords.longitude.toFixed(5)}`; },
        () => {}, { timeout: 4000 },
      );
    }
    cameraInputRef.current?.click();
  };

  // Send a typing heartbeat — throttled so we don't hammer the server on
  // every keystroke.
  const heartbeat = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingSentRef.current < TYPING_HEARTBEAT_MS) return;
    lastTypingSentRef.current = now;
    api.post(`/chat/channels/${channel.id}/typing`).catch(() => {});
  }, [channel.id]);

  // Toggle a reaction on a message
  const toggleReaction = useCallback(async (msgId, emoji) => {
    // Optimistic UI: flip locally first, then sync server response.
    setMessages((arr) => arr.map((m) => {
      if (m.id !== msgId) return m;
      const reactions = { ...(m.reactions || {}) };
      const list = [...(reactions[emoji] || [])];
      const idx = list.indexOf(user.id);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(user.id);
      if (list.length) reactions[emoji] = list;
      else delete reactions[emoji];
      return { ...m, reactions };
    }));
    setReactPickerFor(null);
    try {
      const { data } = await api.post(`/chat/messages/${msgId}/react`, { emoji });
      setMessages((arr) => arr.map((m) => (m.id === msgId ? { ...m, reactions: data.reactions } : m)));
    } catch {
      toast.error("Reaction failed");
      loadMessages(true);
    }
  }, [user.id, loadMessages]);

  // Detect @ to open mention picker
  const onDraftChange = (val) => {
    setDraft(val);
    if (val.trim()) heartbeat();
    const ta = textareaRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart;
    const upToCursor = val.slice(0, cursor);
    const m = upToCursor.match(/@([\w-]*)$/);
    if (m) { setMentionOpen(true); setMentionQuery(m[1]); }
    else setMentionOpen(false);
  };

  const insertMention = (member) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart;
    const before = draft.slice(0, cursor).replace(/@([\w-]*)$/, "");
    const after = draft.slice(cursor);
    const tag = `@${member.name.replace(/\s+/g, "-")} `;
    const next = before + tag + after;
    setDraft(next);
    setMentionOpen(false);
    setTimeout(() => {
      ta.focus();
      const pos = (before + tag).length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const insertEmoji = (emoji) => {
    const ta = textareaRef.current;
    const cursor = ta ? ta.selectionStart : draft.length;
    const next = draft.slice(0, cursor) + emoji + draft.slice(cursor);
    setDraft(next);
    setEmojiOpen(false);
    setTimeout(() => {
      if (!ta) return;
      ta.focus();
      const pos = cursor + emoji.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const onPhotoChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    const items = files.map((f, i) => ({ key: `${Date.now()}-${i}`, file: f, url: URL.createObjectURL(f), pct: 0 }));
    setUploads((u) => [...u, ...items]);
    await uploadMany(items, "chat", {
      onProgress: (key, pct) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct } : x))),
      onDone: (key, res) => {
        setPhotos((p) => [...p, res.path]);
        setUploads((u) => {
          const it = u.find((x) => x.key === key);
          if (it?.url) URL.revokeObjectURL(it.url);
          return u.filter((x) => x.key !== key);
        });
      },
      onError: (key, err) => {
        toast.error(err?.response?.data?.detail || "Photo upload failed");
        setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: true } : x)));
        setTimeout(() => setUploads((u) => u.filter((x) => x.key !== key)), 4000);
      },
    });
    setUploading(false);
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if (sending) return;
    if (!draft.trim() && photos.length === 0) return;
    // Resolve @mentions to user ids
    const mentions = team
      .filter((m) => draft.includes(`@${m.name.replace(/\s+/g, "-")}`))
      .map((m) => m.id);
    setSending(true);
    try {
      await api.post(`/chat/channels/${channel.id}/messages`, {
        text: draft.trim(),
        photo_paths: photos,
        mentions,
        location: photos.length > 0 ? locationRef.current : null,
      });
      locationRef.current = null;
      setDraft("");
      setPhotos([]);
      await loadMessages(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const onDelete = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/chat/messages/${m.id}`);
      await loadMessages(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  const mentionResults = mentionOpen
    ? team.filter((m) => m.name.toLowerCase().includes(mentionQuery.toLowerCase()) && m.id !== user.id).slice(0, 6)
    : [];

  return (
    <>
      {/* Header — Crew Chat style */}
      <div className="px-2 py-2.5 flex items-center gap-2 bg-[#0B1018] shrink-0">
        <button type="button" onClick={() => nav("/app")} className="p-1.5 text-zinc-100 hover:text-[#FF5F15]" data-testid="chat-back-btn" aria-label="Back">
          <ArrowLeft className="w-6 h-6" strokeWidth={2.2} />
        </button>
        <div className="w-11 h-11 shrink-0 rounded-2xl overflow-hidden bg-[#FF5F15] flex items-center justify-center">
          <img src="/icon-192.png" alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-black tracking-tight text-[19px] truncate text-white leading-tight">
            Crew Chat
          </div>
          <div className="text-[12px] text-zinc-400 truncate">
            {channel.name || "General"} · all staff
          </div>
        </div>
        <button type="button" onClick={() => setCallOpen(true)} className="p-2 text-zinc-200 hover:text-[#FF5F15]" data-testid="chat-call-btn" aria-label="Call a crew member">
          <Phone className="w-[22px] h-[22px]" strokeWidth={1.8} />
        </button>
        <button type="button" onClick={() => setCallOpen(true)} className="p-2 text-zinc-200 hover:text-[#FF5F15]" data-testid="chat-video-btn" aria-label="Video call">
          <Video className="w-[22px] h-[22px]" strokeWidth={1.8} />
        </button>
        <button type="button" onClick={() => setTab(tab === "details" ? "chat" : "details")} className="p-2 text-zinc-200 hover:text-[#FF5F15] relative" data-testid="chat-menu-btn" aria-label="Channel details">
          <MoreHorizontal className="w-[22px] h-[22px]" strokeWidth={1.8} />
          {pins.length > 0 && <span className="absolute top-1 right-0.5 w-2 h-2 bg-[#FF5F15] rounded-full" />}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-zinc-800/70 bg-[#0B1018] shrink-0 px-1" data-testid="chat-tabs">
        {[["chat", "Chat"], ["files", "Files"], ["tasks", "Tasks"], ["details", "Details"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
                  className={`flex-1 py-2.5 text-[13px] font-bold border-b-2 transition-colors inline-flex items-center justify-center gap-1.5 ${
                    tab === k ? "border-[#FF5F15] text-[#FF5F15]" : "border-transparent text-zinc-500 hover:text-zinc-200"
                  }`}
                  data-testid={`chat-tab-${k}`}>
            {label}
            {k === "tasks" && openTasks > 0 && (
              <span className="bg-[#FF5F15] text-white text-[9px] font-mono font-bold w-4 h-4 rounded-full leading-none flex items-center justify-center">{openTasks}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "chat" && (
        <>
          <MembersStrip members={members} onCall={() => setCallOpen(true)}
                        canInvite={["admin", "office", "super_admin"].includes(user.role)} />
          <PinnedStrip pins={pins} onOpenAll={() => setTab("details")} />
        </>
      )}

      {tab === "files" && <FilesTab channelId={channel.id} onPhotoClick={onPhotoClick} />}
      {tab === "tasks" && <TasksTab refreshKey={tasksRefresh} onNewTask={() => setTaskOpen(true)} />}
      {tab === "details" && (
        <DetailsTab members={members} pins={pins} onUnpin={togglePin}
                    canPin={["admin", "office", "super_admin"].includes(user.role)} />
      )}

      {tab === "chat" && (
      <>
      {/* Messages */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1.5 bg-[#0B1018]" data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
            No messages yet. Say kia ora 👷
          </div>
        ) : (
          renderMessageList(messages, user, onPhotoClick, onDelete, team, toggleReaction, reactPickerFor, setReactPickerFor, seenByForMessage, togglePin, setSiteNoteFor)
        )}
      </div>

      {/* Typing indicator */}
      <TypingIndicator typing={typing} />

      {/* Photo preview row */}
      {(photos.length > 0 || uploads.length > 0) && (
        <div
          className={`${mobileBarClasses} bg-[#0B1018] p-2 flex gap-2 overflow-x-auto shrink-0`}
          data-testid="chat-photo-preview">
          {photos.map((p, i) => (
            <div key={p} className="relative shrink-0 w-16 h-16 rounded-xl overflow-hidden border border-zinc-700 bg-[#101620]">
              <AuthImage path={p} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => setPhotos((arr) => arr.filter((_, j) => j !== i))}
                className="absolute top-0.5 right-0.5 bg-zinc-900/80 text-white rounded-full p-0.5 hover:bg-red-600"
                data-testid={`chat-photo-remove-${i}`}>
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {uploads.map((u) => (
            <UploadingThumb key={u.key} up={u} className="!aspect-auto shrink-0 w-16 h-16 rounded-xl" />
          ))}
        </div>
      )}

      {/* Composer */}
      <form
        onSubmit={send}
        className={`${mobileBarClasses} p-2.5 relative shrink-0 bg-[#0B1018]`}
        data-testid="chat-composer">
        {mentionResults.length > 0 && (
          <div className="absolute bottom-full left-3 right-3 mb-1 bg-[#1A1B1E] border border-zinc-700 rounded-xl shadow-lg max-h-48 overflow-y-auto z-10" data-testid="mention-picker">
            {mentionResults.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => insertMention(m)}
                className="w-full text-left px-3 py-2 hover:bg-zinc-800 flex items-center gap-2 text-sm text-zinc-100"
                data-testid={`mention-${m.id}`}>
                <AtSign className="w-3 h-3 text-[#FF5F15]" />
                <span className="font-semibold">{m.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500 ml-auto">{m.role}</span>
              </button>
            ))}
          </div>
        )}
        {emojiOpen && (
          <div className="absolute bottom-full left-3 right-3 mb-2 bg-[#1A1B1E] border border-zinc-700 rounded-2xl shadow-lg p-2 z-20" data-testid="emoji-picker">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 px-1 pb-1.5 flex items-center justify-between">
              <span>Emojis</span>
              <button type="button" onClick={() => setEmojiOpen(false)} className="text-zinc-400 hover:text-zinc-700">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-10 gap-0.5 max-h-44 overflow-y-auto">
              {COMPOSER_EMOJIS.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => insertEmoji(e)}
                  className="text-xl w-8 h-8 flex items-center justify-center rounded hover:bg-zinc-800 active:scale-90 transition-all"
                  data-testid={`emoji-${e}`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-2 items-end">
          {/* + attach (gallery) */}
          <label className="cursor-pointer w-12 h-12 rounded-full bg-[#151C27] text-zinc-200 hover:text-[#FF5F15] flex items-center justify-center shrink-0 transition-colors" data-testid="chat-gallery-button" aria-label="Attach photos">
            <Plus className="w-6 h-6" strokeWidth={1.9} />
            <input type="file" accept="image/*" multiple
              className="hidden" onChange={onPhotoChange} disabled={uploading} />
          </label>
          <div className="flex-1 flex items-end gap-0.5 bg-transparent border border-zinc-700 rounded-full px-2 py-1 focus-within:border-[#FF5F15]/60 transition-colors">
            <Textarea
              ref={textareaRef}
              rows={1}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !mentionOpen) { e.preventDefault(); send(); }
                if (e.key === "Escape") { setMentionOpen(false); setEmojiOpen(false); }
              }}
              placeholder={uploading ? "Uploading…" : "Type a message…"}
              className="flex-1 resize-none min-h-[36px] max-h-32 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none text-sm py-2 px-1.5 text-zinc-100 placeholder:text-zinc-500"
              data-testid="chat-input"
            />
            <button
              type="button"
              onClick={() => setEmojiOpen((v) => !v)}
              className={`p-1.5 rounded-full transition-colors ${emojiOpen ? "text-[#FF5F15]" : "text-zinc-400 hover:text-[#FF5F15]"}`}
              data-testid="chat-emoji-button"
              aria-label="Open emoji picker"
            >
              <Smile className="w-5 h-5" />
            </button>
            {/* Camera inside the input, like the mockup */}
            <label className="cursor-pointer p-1.5 text-zinc-400 hover:text-[#FF5F15] transition-colors" data-testid="chat-camera-button" aria-label="Take photo with camera">
              <Camera className="w-5 h-5" />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                className="hidden" onChange={onPhotoChange} disabled={uploading} />
            </label>
          </div>
          {(draft.trim() || photos.length > 0) ? (
            <button
              type="submit"
              disabled={sending || uploading}
              className="w-12 h-12 rounded-full bg-[#FF5F15] hover:bg-[#E65100] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
              data-testid="chat-send-button"
              aria-label="Send message">
              <Send className="w-[18px] h-[18px]" />
            </button>
          ) : (
            <VoiceButton onSend={sendVoice} disabled={uploading || sending} />
          )}
        </div>
      </form>

      {/* Quick actions */}
      <QuickActionsBar
        onSiteNote={() => {
          const lastText = [...messages].reverse().find((m) => (m.text || "").trim() && !m.deleted_at);
          if (lastText) setSiteNoteFor(lastText);
          else toast.message("Send a text message first, then save it as a site note");
        }}
        onHazard={() => nav("/app/safety")}
        onPhoto={quickPhoto}
        onTask={() => setTaskOpen(true)}
      />
      </>
      )}

      <CallSheet open={callOpen} onClose={() => setCallOpen(false)} members={members} />
      <SiteNoteDialog
        open={!!siteNoteFor}
        message={siteNoteFor}
        onClose={() => setSiteNoteFor(null)}
        onDone={(mid, siteNote) => {
          setMessages((arr) => arr.map((m) => (m.id === mid ? { ...m, site_note: siteNote } : m)));
        }}
      />
      <TaskDialog open={taskOpen} onClose={() => setTaskOpen(false)} onCreated={() => { setTasksRefresh((v) => v + 1); loadExtras(); }} />
    </>
  );
}

function TypingIndicator({ typing }) {
  if (!typing || typing.length === 0) return null;
  const names = typing.map((t) => t.user_name?.split(" ")[0] || "Someone");
  const summary = names.length === 1
    ? `${names[0]} is typing`
    : names.length === 2
      ? `${names[0]} and ${names[1]} are typing`
      : `${names[0]}, ${names[1]} and ${names.length - 2} more are typing`;
  return (
    <div className="px-4 py-1.5 text-[11px] font-mono text-zinc-500 bg-[#0B1018] flex items-center gap-2 shrink-0" data-testid="chat-typing">
      <span className="flex gap-0.5">
        <span className="w-1.5 h-1.5 bg-[#FF5F15] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-1.5 h-1.5 bg-[#FF5F15] rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
        <span className="w-1.5 h-1.5 bg-[#FF5F15] rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
      </span>
      <span>{summary}…</span>
    </div>
  );
}

function canDelete(msg, user) {
  if (msg.deleted_at) return false;
  if (user.role === "admin" || user.role === "office" || user.role === "super_admin") return true;
  if (msg.user_id !== user.id) return false;
  return (Date.now() - new Date(msg.created_at).getTime()) < DELETE_WINDOW_MS;
}

function dayLabel(d) {
  const today = new Date();
  const date = new Date(d);
  const diff = Math.floor((today.setHours(0,0,0,0) - new Date(d).setHours(0,0,0,0)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return date.toLocaleDateString("en-NZ", { weekday: "long" });
  return date.toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: date.getFullYear() === today.getFullYear() ? undefined : "numeric" });
}

function renderMessageList(messages, user, onPhotoClick, onDelete, team, toggleReaction, reactPickerFor, setReactPickerFor, seenByForMessage, togglePin, setSiteNoteFor) {
  const out = [];
  let lastDay = "";
  messages.forEach((m, i) => {
    const day = new Date(m.created_at).toDateString();
    if (day !== lastDay) {
      out.push(
        <div key={`d-${day}`} className="flex items-center gap-3 my-4" data-testid="chat-day-separator">
          <span className="flex-1 h-px bg-zinc-800" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            {dayLabel(m.created_at)}
          </span>
          <span className="flex-1 h-px bg-zinc-800" />
        </div>
      );
      lastDay = day;
    }
    if (m.system) {
      const t = new Date(m.created_at).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" });
      out.push(
        <div key={m.id} className="flex justify-center my-2 px-4" data-testid={`system-msg-${m.id}`}>
          <div className="inline-flex items-center gap-2 bg-[#151C27] border border-[#FF5F15]/25 rounded-full px-3.5 py-1.5 max-w-full">
            <Bell className="w-3.5 h-3.5 text-[#FF5F15] shrink-0" />
            <span className="text-[11px] text-zinc-300 leading-snug min-w-0">{m.text}</span>
            <span className="text-[10px] text-zinc-500 shrink-0">{t}</span>
          </div>
        </div>
      );
      return;
    }
    const mine = m.user_id === user.id;
    const prev = messages[i - 1];
    const sameAuthor = prev && prev.user_id === m.user_id &&
      new Date(prev.created_at).toDateString() === day &&
      (new Date(m.created_at) - new Date(prev.created_at) < 5 * 60_000);
    out.push(
      <MessageBubble
        key={m.id}
        msg={m}
        mine={mine}
        showAuthor={!sameAuthor}
        groupedTop={sameAuthor}
        onPhotoClick={onPhotoClick}
        onDelete={onDelete}
        canDelete={canDelete(m, user)}
        team={team}
        currentUserId={user.id}
        toggleReaction={toggleReaction}
        pickerOpen={reactPickerFor === m.id}
        onOpenPicker={() => setReactPickerFor((id) => id === m.id ? null : m.id)}
        onClosePicker={() => setReactPickerFor(null)}
        seenBy={seenByForMessage?.[m.id] || []}
        onPin={() => togglePin(m.id)}
        onSiteNote={() => { setSiteNoteFor(m); setReactPickerFor(null); }}
      />
    );
  });
  return out;
}

function MessageBubble({ msg, mine, showAuthor, groupedTop, onPhotoClick, onDelete, canDelete, team, currentUserId, toggleReaction, pickerOpen, onOpenPicker, onClosePicker, seenBy, onPin, onSiteNote }) {
  const longPressTimer = useRef(null);
  const longPressedRef = useRef(false);
  const startLongPress = () => {
    longPressedRef.current = false;
    clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      longPressedRef.current = true;
      if (navigator.vibrate) navigator.vibrate(10);
      onOpenPicker?.();
    }, 420);
  };
  const cancelLongPress = () => clearTimeout(longPressTimer.current);
  if (msg.deleted_at) {
    return (
      <div className={`text-[11px] italic text-zinc-400 px-2 ${mine ? "text-right" : ""}`}>
        Message deleted
      </div>
    );
  }
  const time = new Date(msg.created_at).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" });
  // Modern bubble corner radius: pointy on the sender side
  const bubbleCorners = mine
    ? "rounded-2xl rounded-tr-md"
    : "rounded-2xl rounded-tl-md";
  const reactions = msg.reactions || {};
  const reactionEntries = Object.entries(reactions).filter(([, ids]) => Array.isArray(ids) && ids.length > 0);

  return (
    <div className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"} ${groupedTop ? "mt-0.5" : "mt-2"}`} data-testid={`msg-${msg.id}`}>
      {!mine && (
        <div className={groupedTop ? "invisible" : ""}>
          <Avatar name={msg.user_name} path={msg.author_avatar_path} size={28} />
        </div>
      )}
      <div className={`max-w-[78%] group flex flex-col relative ${mine ? "items-end" : "items-start"}`}>
        {showAuthor && !mine && (
          <div className="text-[13px] font-bold text-zinc-100 mb-1 pl-1">
            {msg.user_name} <span className="text-[10px] font-normal text-zinc-500 ml-1">{time}</span>
          </div>
        )}
        {mine && showAuthor && (
          <div className="text-[10px] text-zinc-500 mb-1 pr-1 flex items-center gap-1">
            {time}
            <CheckCheck className={`w-3.5 h-3.5 ${seenBy && seenBy.length > 0 ? "text-[#FF5F15]" : "text-zinc-600"}`} />
          </div>
        )}
        <div
          className={`relative px-3.5 py-2.5 ${bubbleCorners} ${
            mine
              ? "bg-[#FF5F15] text-white"
              : "bg-[#151C27] text-zinc-100"
          }`}
          onDoubleClick={(e) => { e.preventDefault(); onOpenPicker?.(); }}
          onTouchStart={startLongPress}
          onTouchEnd={cancelLongPress}
          onTouchMove={cancelLongPress}
          onContextMenu={(e) => { e.preventDefault(); onOpenPicker?.(); }}
        >
          {msg.photo_paths?.length > 0 && (
            <div className={`grid ${msg.photo_paths.length === 1 ? "grid-cols-1" : "grid-cols-2"} gap-1 ${msg.text ? "mb-2" : ""} -mx-1 -mt-1`}>
              {msg.photo_paths.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => {
                    if (longPressedRef.current) { longPressedRef.current = false; return; }
                    onPhotoClick?.(p);
                  }}
                  className="overflow-hidden rounded-lg hover:opacity-90 transition-opacity"
                  data-testid={`msg-photo-${msg.id}`}>
                  <AuthImage path={p} alt="" className="w-full max-h-64 object-cover" />
                </button>
              ))}
            </div>
          )}
          {msg.audio_path && (
            <AudioBubble path={msg.audio_path} seconds={msg.audio_seconds} mine={mine} />
          )}
          {msg.text && (
            <div className="text-sm whitespace-pre-wrap break-words leading-snug">
              {renderText(msg.text, team, mine)}
            </div>
          )}
          <LocationPin location={msg.location} mine={mine} />
          {/* Reaction button — small floating SmilePlus on hover (desktop) or tap on mobile */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenPicker?.(); }}
            className={`absolute -top-2 ${mine ? "-left-7" : "-right-7"} w-6 h-6 rounded-full bg-[#1A1B1E] border border-zinc-700 shadow flex items-center justify-center text-zinc-300 hover:text-[#FF5F15] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity`}
            data-testid={`msg-react-${msg.id}`}
            aria-label="Add reaction"
          >
            <SmilePlus className="w-3.5 h-3.5" />
          </button>
          {/* Reaction picker popover */}
          {pickerOpen && (
            <div
              className={`absolute z-20 -top-12 ${mine ? "right-0" : "left-0"} bg-[#1A1B1E] border border-zinc-700 rounded-full shadow-lg px-1.5 py-1 flex items-center gap-0.5`}
              data-testid={`msg-react-picker-${msg.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              {REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleReaction(msg.id, emoji); }}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-lg hover:bg-zinc-800 active:scale-90 transition-all"
                  data-testid={`msg-react-emoji-${msg.id}-${emoji}`}
                >
                  {emoji}
                </button>
              ))}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onPin?.(); }}
                className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-300 hover:bg-zinc-800"
                data-testid={`msg-pin-${msg.id}`}
                aria-label={msg.pinned ? "Unpin message" : "Pin message"}
              >
                <Pin className={`w-4 h-4 ${msg.pinned ? "text-[#FF5F15]" : ""}`} />
              </button>
              {(msg.text || "").trim() && !msg.site_note && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onSiteNote?.(); }}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-300 hover:bg-zinc-800"
                  data-testid={`msg-site-note-${msg.id}`}
                  aria-label="Save as site note"
                >
                  <StickyNote className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onClosePicker?.(); }}
                className="w-7 h-7 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                aria-label="Close reaction picker"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
        {/* Site note chip */}
        {msg.site_note && <SiteNoteChip siteNote={msg.site_note} messageId={msg.id} />}
        {/* Reaction chips */}
        {reactionEntries.length > 0 && (
          <div className={`mt-1 flex flex-wrap gap-1 ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-reactions-${msg.id}`}>
            {reactionEntries.map(([emoji, ids]) => {
              const reacted = ids.includes(currentUserId);
              return (
                <button
                  type="button"
                  key={emoji}
                  onClick={() => toggleReaction(msg.id, emoji)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border transition-colors ${
                    reacted
                      ? "bg-[#FF5F15]/20 border-[#FF5F15] text-orange-200"
                      : "bg-[#1A1B1E] border-zinc-700/80 text-zinc-200 hover:border-zinc-500"
                  }`}
                  data-testid={`msg-reaction-chip-${msg.id}-${emoji}`}
                >
                  <span>{emoji}</span>
                  <span className="font-mono text-[10px]">{ids.length}</span>
                </button>
              );
            })}
          </div>
        )}
        {!showAuthor && (
          <div className={`text-[10px] mt-0.5 px-1 text-zinc-600 flex items-center gap-1 ${mine ? "justify-end" : ""}`}>
            {time}
            {mine && <CheckCheck className={`w-3 h-3 ${seenBy && seenBy.length > 0 ? "text-[#FF5F15]" : "text-zinc-600"}`} />}
          </div>
        )}
        {canDelete && (
          <button type="button"
            onClick={() => onDelete(msg)}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] text-zinc-400 hover:text-red-600 flex items-center gap-1 px-1"
            data-testid={`msg-delete-${msg.id}`}>
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        )}
      </div>
      {mine && (
        <div className={groupedTop ? "invisible" : ""}>
          <Avatar name={msg.user_name} path={msg.author_avatar_path} size={28} />
        </div>
      )}
    </div>
  );
}

// Render @Name tokens highlighted (color adjusts to bubble bg)
function renderText(text, team, mine) {
  const names = team.map((m) => m.name.replace(/\s+/g, "-"));
  const re = new RegExp("(@(?:" + names.map(escapeRe).join("|") + "))", "g");
  if (!names.length) return text;
  const parts = text.split(re);
  return parts.map((p, i) => {
    if (p?.startsWith("@") && names.includes(p.slice(1))) {
      return (
        <span key={i} className={`font-bold px-1 rounded ${mine ? "bg-white/20 text-white" : "text-[#FF5F15] bg-orange-100/80"}`}>
          {p.replace(/-/g, " ")}
        </span>
      );
    }
    return <span key={i}>{p}</span>;
  });
}
function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }
